import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "#/lib/queryKeys";
import {
  createOutlineBacklogFromConcept,
  materializeRelatedMedicalConcepts,
  regenerateOutlineForMedicalConcept,
  RelatedConceptSuggestion,
  suggestRelatedMedicalConcepts,
  triggerOutlineBacklogById,
} from "#/services/articleOutlines";

export function useMedicalConceptDetailMutations(conceptId: string) {
  const queryClient = useQueryClient();

  const triggerOutlineFromConcept = useMutation({
    mutationFn: async (payload: { conceptName: string }) => {
      const created = await createOutlineBacklogFromConcept({
        conceptId,
        topicText: payload.conceptName,
        suggestedTitle: payload.conceptName,
        source: "webapp_manual_concept_trigger",
      });

      const createdOutlineId = created?.item?.id;
      if (!(typeof createdOutlineId === "string" && createdOutlineId.length > 0)) {
        throw new Error("Backlog item was created without an id.");
      }

      return triggerOutlineBacklogById(createdOutlineId);
    },
  });

  const suggestRelated = useMutation({
    mutationFn: async (limit: number) =>
      suggestRelatedMedicalConcepts({
        seedConceptId: conceptId,
        limit: Math.max(1, limit || 1),
      }),
  });

  const regenerateOutline = useMutation({
    mutationFn: async () => regenerateOutlineForMedicalConcept(conceptId),
  });

  const materializeRelated = useMutation({
    mutationFn: async (items: RelatedConceptSuggestion[]) =>
      materializeRelatedMedicalConcepts({
        seedConceptId: conceptId,
        items,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.medicalConceptsList() });
      queryClient.invalidateQueries({ queryKey: qk.medicalConceptDetail(conceptId) });
    },
  });

  return {
    triggerOutlineFromConcept,
    regenerateOutline,
    suggestRelated,
    materializeRelated,
  };
}

export default useMedicalConceptDetailMutations;
